<script setup>
import headerNavigation from '@/config/header-navigation';

import routes from '@/constants/routes';
import analyticEventKeys from '@/constants/analytic-event-keys';
import { dropdownTriggers } from '@/constants/dropdown';

const analytics = useGoogleAnalytics();

const props = defineProps({
  isFormVisible: {
    type: Boolean,
    default: true,
  },
  isHeadingVisible: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const authStore = useAuthStore();

// breakpoint
const breakpoint = useCustomBreakpoints();
const breakpointDownSm = breakpoint.smaller('sm');

// items state
const isLogoVisible = computed(() => !props.isHeadingVisible);
const isFormButtonVisible = computed(() => !props.isFormVisible);
const accountButtonsCount = computed(() => {
  let count = 1;

  if (authStore.account?.activeOrderTicketsCount || !authStore.isGuest)
    count += 1;
  if (authStore.account?.activeCartTicketsCount) count += 1;
  if (isFormButtonVisible.value) count += 1;

  return count;
});
const isSwitchesVisible = computed(
  () => !isLogoVisible.value || accountButtonsCount.value < 3,
);

// Logo scroll to top
const route = useRoute();
const localeRoute = useLocaleRoute();

function handleLogoClick() {
  if (route.name === localeRoute('index').name) scrollToTop();

  handleRedirect(routes.MAIN);
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
}

// Redirect
function handleRedirect(link) {
  analytics.pushEvent({
    event: analyticEventKeys.MENU_NAVIGATE,
    label: link,
  });
}

// Mobile menu
const headerRef = ref(null);
const accountButtonsRef = ref(null);

const isMenuOpened = ref(false);

function handleAccountButtonClick() {
  if (!accountButtonsRef.value) return;
  accountButtonsRef.value.handleAuthClick();
}
function handleMenuVisibilityChange(value) {
  isMenuOpened.value = value;
}

// Schema org
const breadcrumbs = computed(() => {
  return headerNavigation.map((item) => {
    return { name: t(item.text), item: item.link };
  });
});

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: breadcrumbs,
  }),
]);
</script>

<template>
  <header
    ref="headerRef"
    class="layout-header"
    :class="{ 'layout-header--with-menu': isMenuOpened }"
  >
    <CTagDetect
      class="hidden-desktop"
      :class="{ 'hidden-mobile': !isLogoVisible }"
      :to="routes.MAIN"
      aria-label="Railmonsters logo"
      @click="handleLogoClick"
    >
      <CImage
        class="layout-header__logo"
        src="/icons/logo-mobile.svg"
        loading="lazy"
        alt="Rail Monsters"
      />
    </CTagDetect>

    <nav class="layout-header__navigation hidden-mobile">
      <LayoutHeaderNavigationItem
        v-for="(item, index) in headerNavigation"
        :key="index"
        class="layout-header__link"
        :to="item.link"
        :text="item.text"
        :reference-ref="headerRef"
        @redirect="handleRedirect"
      />
    </nav>

    <BlocksAward class="layout-header__award hidden-mobile" />

    <div
      class="layout-header__switchers"
      :class="{ 'hidden-mobile': !isSwitchesVisible }"
    >
      <BundlesCurrencySelect
        :trigger-event="
          breakpointDownSm ? dropdownTriggers.click : dropdownTriggers.hover
        "
      />
      <BundlesLangSelect
        :trigger-event="
          breakpointDownSm ? dropdownTriggers.click : dropdownTriggers.hover
        "
      />
    </div>

    <div class="layout-header__wrapper hidden-desktop">
      <ClientOnly>
        <BundlesAccountNavigationButtons
          ref="accountButtonsRef"
          class="layout-header__buttons"
          :is-form-button-visible="isFormButtonVisible"
          @search-click="scrollToTop"
        />
      </ClientOnly>
    </div>

    <UiDropdownMenu
      class="hidden-desktop"
      :reference-ref="headerRef"
      :show-switches="!isSwitchesVisible"
      :show-search-button="isFormButtonVisible"
      @redirect="handleRedirect"
      @search-click="scrollToTop"
      @account-click="handleAccountButtonClick"
      @visibility-change="handleMenuVisibilityChange"
    />
  </header>
</template>
